import moment from 'moment';
import {
  VAR_CUSTOMER_NAME,
  VAR_DATE_AND_START_TIME,
  VAR_SERVICE_NAME,
  VAR_PROFESSIONAL_NAME,
  VAR_COURT_NAME,
  VAR_TOTAL_SERVICE_VALUE_NAME,
  VAR_START_TIME,
  VAR_END_TIME,
  VAR_FULL_CUSTOMER_NAME,
  VAR_DATE_FORMAT_1,
  VAR_DATE_FORMAT_2,
} from './messageConstants';
import NumberUtils from './numberUtils';
import { isAppTypeCourtRental } from 'commons/helpers/appTypeSettings';

const CALENDAR_FORMAT_CONFIG = {
  sameDay: '([Hoje]) D [de] MMMM',
  nextDay: '([Amanhã]) D [de] MMMM',
  lastDay: 'D [de] MMMM',
  lastWeek: 'D [de] MMMM',
  nextWeek: '(dddd) D [de] MMMM',
  sameElse: '(dddd) D [de] MMMM',
};

export function prepareDateAndTime({ date, startTime }) {
  const formattedDate = moment(date).format('(ddd) DD/MM');
  return `${formattedDate} às ${startTime}`;
}

export function prepareProfessionalName({ isCourtRental, professionalName }) {
  const nameParts = isCourtRental ? [professionalName.trim()] : professionalName.trim().split(' ');
  return nameParts.length > 0 ? nameParts[0] : '';
}

export function prepareCustomerName({ customerName }) {
  const nameParts = customerName.trim().split(' ');
  return nameParts.length > 0 ? nameParts[0] : '';
}

export function prepareMessageForAllVariables({
  message,
  customerName,
  serviceName,
  date,
  startTime,
  endTime,
  professionalName,
  totalServiceValue,
  isCourtRental,
}) {
  let selectedMsgString = message;
  if (customerName) {
    if (selectedMsgString.includes(VAR_CUSTOMER_NAME)) {
      const nameParts = customerName.trim().split(' ');
      if (nameParts.length > 0) {
        selectedMsgString = selectedMsgString.replace(VAR_CUSTOMER_NAME, nameParts[0]);
      } else {
        selectedMsgString = selectedMsgString.replace(VAR_CUSTOMER_NAME, '');
      }
    } else if (selectedMsgString.includes(VAR_FULL_CUSTOMER_NAME)) {
      selectedMsgString = selectedMsgString.replace(VAR_FULL_CUSTOMER_NAME, customerName);
    }
  } else {
    selectedMsgString = selectedMsgString.replace(VAR_CUSTOMER_NAME, '');
  }

  if (professionalName) {
    if (isCourtRental) {
      selectedMsgString = selectedMsgString.replace(VAR_COURT_NAME, professionalName.trim());
    } else {
      // eslint-disable-next-line no-lonely-if
      if (selectedMsgString.includes(VAR_PROFESSIONAL_NAME)) {
        const nameParts = professionalName.trim().split(' ');
        if (nameParts.length > 0) {
          selectedMsgString = selectedMsgString.replace(VAR_PROFESSIONAL_NAME, nameParts[0]);
        } else {
          selectedMsgString = selectedMsgString.replace(VAR_PROFESSIONAL_NAME, '');
        }
      }
    }
  } else {
    selectedMsgString = selectedMsgString.replace(isCourtRental ? VAR_COURT_NAME : VAR_PROFESSIONAL_NAME, '');
  }

  if (serviceName) {
    selectedMsgString = selectedMsgString.replace(VAR_SERVICE_NAME, serviceName);
  }
  if (date && startTime) {
    const formattedDate = moment(date).calendar(null, CALENDAR_FORMAT_CONFIG);
    selectedMsgString = selectedMsgString.replace(
      VAR_DATE_AND_START_TIME,
      `dia ${formattedDate} às ${startTime}`,
    );
  }

  if (
    selectedMsgString.includes(VAR_TOTAL_SERVICE_VALUE_NAME) &&
    totalServiceValue != null &&
    totalServiceValue !== ''
  ) {
    selectedMsgString = selectedMsgString.replace(
      VAR_TOTAL_SERVICE_VALUE_NAME,
      NumberUtils.toCurrency(totalServiceValue),
    );
  } else {
    selectedMsgString = selectedMsgString.replace(VAR_TOTAL_SERVICE_VALUE_NAME, '-');
  }

  if (selectedMsgString.includes(VAR_DATE_FORMAT_1) && date) {
    const formattedDate = moment(date).format('DD/MM/YYYY');
    selectedMsgString = selectedMsgString.replace(VAR_DATE_FORMAT_1, formattedDate);
  }
  if (selectedMsgString.includes(VAR_DATE_FORMAT_2) && date) {
    const formattedDate = moment(date).format('DD [de] MMMM');
    selectedMsgString = selectedMsgString.replace(VAR_DATE_FORMAT_2, formattedDate);
  }
  if (selectedMsgString.includes(VAR_START_TIME) && startTime) {
    selectedMsgString = selectedMsgString.replace(VAR_START_TIME, startTime);
  }
  if (selectedMsgString.includes(VAR_END_TIME) && endTime) {
    selectedMsgString = selectedMsgString.replace(VAR_END_TIME, endTime);
  }

  return selectedMsgString;
}

// MULTI MESSAGE
// //////////////

export function getMultiMessageSettingsFromString(settingsString) {
  if (settingsString != null && settingsString.startsWith('multi:')) {
    const settings = settingsString.substring('multi:'.length);
    const parts = settings.split(',');
    const hideProfessional = parts.length > 0 ? parts[0] === 'true' : false;
    const goodbyeText = parts.length > 1 ? parts[1] : 'Obrigado!';

    return { hideProfessional, goodbyeText };
  }

  return {
    hideProfessional: false,
    goodbyeText: 'Obrigado!',
  };
}

export function prepareMultiMessage({
  customerName,
  appointments,
  isPackage = false,
  hideProfessional = false,
  goodbyeText = 'Obrigado!',
}) {
  const isCourtRental = isAppTypeCourtRental();
  const plural = appointments.length > 1;

  const body = appointments
    .map((app) => {
      const preparedDateTimeFormatted = prepareDateAndTime({ date: app.date, startTime: app.startTime });
      const preparedServiceName = app.serviceName;

      if (hideProfessional) {
        return `📅 ${preparedDateTimeFormatted}
📌 Serviço: ${preparedServiceName}`;
      }
      const preparedProfessionalName = prepareProfessionalName({
        isCourtRental,
        professionalName: app.user.name,
      });
      return `📅 ${preparedDateTimeFormatted}
📌 Serviço: ${preparedServiceName} - Profissional: ${preparedProfessionalName}`;
    })
    .join('\n');

  const preparedCustomerName = prepareCustomerName({
    isCourtRental,
    customerName,
  });
  const message = `Oi ${preparedCustomerName}, tudo bem?

${
  isPackage
    ? plural
      ? 'Este são os agendamentos do seu pacote'
      : 'Este é o agendamento do seu pacote'
    : plural
    ? 'Este é um lembrete para os seus agendamentos'
    : 'Este é um lembrete para o seu agendamento'
}.

${body}

${goodbyeText}`;

  return message;
}
